/**
 * Wie es dem Bild auf *diesem* Gerät geht.
 *
 * „Bei iOS funktioniert das mit dem Ton jetzt super, aber das eingesetzte
 * Video funktioniert leider nicht. Es spielt einfach nicht ab.“ (Sascha,
 * 7. September)
 *
 * Hier steht kein iPhone, und der Prüf-Browser kann nicht einmal H.264
 * dekodieren — die Wiedergabe des Bildes lässt sich in diesem Haus also gar
 * nicht nachstellen. Beim Ton hat genau das geholfen: nicht weiter raten,
 * sondern das Gerät fragen. Also dasselbe noch einmal, für das Bild.
 *
 * Die Werte sind so gewählt, dass sie die Ursachen *auseinanderhalten* — jede
 * Zeile unten schließt etwas anderes aus:
 *
 *   fehler 4 (SRC_NOT_SUPPORTED)  → das Format kann das Gerät nicht.
 *   netz 3 (NO_SOURCE)            → die Datei kam gar nicht an.
 *   bereit 0, kein Fehler         → sie lädt noch (oder Bereichs-Anfragen
 *                                    kommen nicht durch).
 *   pausiert und „abgewiesen“     → das Gerät hat das Abspielen verweigert.
 *   bereit ≥ 2, läuft, Zeit steht → dekodiert, aber kommt nicht voran.
 *   breite 0                      → nie ein Bild dekodiert.
 */

export interface BildBericht {
  /** Welche Fläche: A und B sind die Basisspur, E1, E2 … die Ebenen darüber (7.69m). */
  flaeche: string;
  bereit: number;
  netz: number;
  pausiert: boolean;
  stumm: boolean;
  zeit: number;
  breite: number;
  hoehe: number;
  /** Wie viel des Films schon geladen ist, in Sekunden. */
  geladenBis: number;
  fehler: number | null;
  fehlerText: string;
  /** Was `starte()` am Element mitschreibt: Anläufe, Abweisungen, Doppelte. */
  anlaeufe: number;
  abgewiesen: boolean;
  doppelt: number;
  /** Der Pfad der Quelle, ohne den Schlüssel darin. */
  quelle: string;
  /**
   * Wie viele Bilder das Gerät dekodiert hat und wie viele davon es
   * *ausgelassen* hat.
   *
   * Das ist die Zahl, die „es ruckelt“ von „es ruckelt nicht“ trennt (7.57).
   * Ohne sie raten wir: eine Wiedergabe kann stocken, weil der Dekoder nicht
   * nachkommt (dann fallen Bilder weg), oder weil der Abspielkopf springt
   * (dann fällt keines weg und es sieht trotzdem unruhig aus). Die beiden
   * brauchen entgegengesetzte Behebungen.
   */
  bilder: number;
  ausgelassen: number;
  /** Sucht es gerade -- und wie lange das zuletzt dauerte (7.69l). */
  suchend: boolean;
  suchdauer: number;
  /** Der Freischalter: ob und wie das Element seinen Anlauf aus der Berührung bekam, und warum nicht (7.69m). */
  frei: string;
  grund: string;
  vorab: boolean;
  ton: boolean;
}

const BEREIT = ['nichts', 'Kopfdaten', 'aktuelles Bild', 'etwas voraus', 'genug'];
const NETZ = ['leer', 'untätig', 'lädt', 'keine Quelle'];
const FEHLER = ['', 'abgebrochen', 'Netz', 'Dekodieren', 'Format nicht unterstützt'];

function ohneSchluessel(src: string): string {
  try {
    const url = new URL(src, location.href);
    // Der Schlüssel in `t` ist eine Vollmacht auf die Datei -- der gehört in
    // keinen Fehlerbericht.
    if (url.searchParams.has('t')) url.searchParams.set('t', '…');
    return url.pathname + url.search;
  } catch {
    return '(unlesbar)';
  }
}

/**
 * Dekodierte und ausgelassene Bilder. `getVideoPlaybackQuality` gibt es in
 * Safari und Chrome; wo nicht, bleiben beide auf 0 und die Zeile sagt nichts
 * Falsches, sondern nichts.
 */
function bildzahlen(el: HTMLVideoElement): { bilder: number; ausgelassen: number } {
  const gib = (el as HTMLVideoElement & { getVideoPlaybackQuality?: () => VideoPlaybackQuality }).getVideoPlaybackQuality;
  if (typeof gib !== 'function') return { bilder: 0, ausgelassen: 0 };
  try {
    const q = gib.call(el);
    return { bilder: q.totalVideoFrames ?? 0, ausgelassen: q.droppedVideoFrames ?? 0 };
  } catch {
    return { bilder: 0, ausgelassen: 0 };
  }
}

/**
 * Was die Leinwand tut, wenn die Grafikkarte malt (7.69h) -- ein Satz für
 * das Melde-Blatt. Leer, wenn der alte Weg läuft.
 */
export function leinwandZeile(): string {
  if (typeof window === 'undefined' || !window.__leinwandBericht) return '';
  try {
    const b = JSON.parse(window.__leinwandBericht()) as {
      sekunden: number; gezeichnetJeSekunde: number; hochgeladenJeSekunde: number; msJeUpload: number; msMax: number;
      weg: string; probe: { sub: number; neu: number }; bildtaktJeSekunde: number;
    };
    return [
      'Leinwand: Grafikkarte',
      `${b.gezeichnetJeSekunde}× je s gezeichnet`,
      `${b.hochgeladenJeSekunde} Video-Uploads je s`,
      `${b.msJeUpload} ms je Upload (max ${b.msMax})`,
      `Weg ${b.weg} (Probe: texSubImage2D ${b.probe.sub} ms, texImage2D ${b.probe.neu} ms)`,
      `Bildtakt ${b.bildtaktJeSekunde}/s`,
      `über ${b.sekunden} s`,
    ].join(' · ');
  } catch {
    return 'Leinwand: Grafikkarte, keine Zahlen';
  }
}

export function bildBericht(): BildBericht[] {
  if (typeof document === 'undefined') return [];
  const flaechen = [...document.querySelectorAll<HTMLVideoElement>('.canvas video')];
  return flaechen.map((el, i) => ({
    flaeche: i === 0 ? 'A' : i === 1 ? 'B' : `E${i - 1}`,
    bereit: el.readyState,
    netz: el.networkState,
    pausiert: el.paused,
    stumm: el.muted,
    zeit: Number(el.currentTime.toFixed(2)),
    breite: el.videoWidth,
    hoehe: el.videoHeight,
    geladenBis: el.buffered.length ? Number(el.buffered.end(el.buffered.length - 1).toFixed(2)) : 0,
    fehler: el.error ? el.error.code : null,
    fehlerText: el.error ? `${FEHLER[el.error.code] ?? el.error.code}${el.error.message ? `: ${el.error.message.slice(0, 120)}` : ''}` : '',
    anlaeufe: Number(el.dataset.anlaeufe ?? 0),
    abgewiesen: Boolean(el.dataset.abgewiesen),
    doppelt: Number(el.dataset.doppelt ?? 0),
    quelle: el.currentSrc ? ohneSchluessel(el.currentSrc) : '(keine)',
    suchend: el.seeking,
    suchdauer: Number(el.dataset.suchdauer ?? 0),
    frei: el.dataset.frei ?? '',
    grund: el.dataset.grund ?? '',
    vorab: Boolean(el.dataset.vorab),
    ton: el.dataset.ton === '1',
    ...bildzahlen(el),
  }));
}

/** Dasselbe in einer Zeile, zum Vorlesen und Mitschicken. */
export function bildZeile(b: BildBericht): string {
  return [
    `Fläche ${b.flaeche}`,
    `bereit ${b.bereit} (${BEREIT[b.bereit] ?? '?'})`,
    `Netz ${b.netz} (${NETZ[b.netz] ?? '?'})`,
    b.pausiert ? 'pausiert' : 'läuft',
    `${b.suchend ? 'sucht' : 'steht'}${b.suchdauer ? ` (Suchen ${b.suchdauer} ms)` : ''}`,
    `frei ${b.frei || '-'}${b.grund ? ` (${b.grund})` : ''}${b.vorab ? ', Vorlader' : ''}${b.ton ? ', mit Ton' : ''}`,
    `bei ${b.zeit}s`,
    `geladen bis ${b.geladenBis}s`,
    `${b.breite}×${b.hoehe}`,
    b.fehler ? `FEHLER ${b.fehler} ${b.fehlerText}` : 'kein Fehler',
    `${b.anlaeufe} Anläufe${b.abgewiesen ? ', zuletzt abgewiesen' : ''}${b.doppelt ? `, ${b.doppelt} doppelte` : ''}`,
    b.bilder > 0
      ? `${b.bilder} Bilder, ${b.ausgelassen} ausgelassen (${Math.round((b.ausgelassen / b.bilder) * 100)} %)`
      : 'keine Bildzählung',
  ].join(' · ');
}

/**
 * Der eine Satz, der am ehesten weiterhilft. Bewusst nicht „alles gut“, wenn
 * es nicht alles gut ist -- die Werte stehen daneben, und wer sie liest, soll
 * ihnen trauen können.
 */
export function bildSatz(berichte: BildBericht[]): string {
  const mitQuelle = berichte.filter((b) => b.quelle !== '(keine)');
  if (mitQuelle.length === 0) return 'Es liegt gerade kein Video unter dem Abspielkopf.';
  const kaputt = mitQuelle.find((b) => b.fehler);
  if (kaputt) {
    return kaputt.fehler === 4
      ? 'Dieses Gerät kann das Format der Vorschau-Datei nicht abspielen. Das ist kein Fehler der Wiedergabe, sondern des Formats.'
      : `Das Bild kam nicht durch: ${kaputt.fehlerText}.`;
  }
  const leer = mitQuelle.find((b) => b.netz === 3);
  if (leer) return 'Die Vorschau-Datei wurde nicht gefunden — die Adresse antwortet nicht.';
  const laedt = mitQuelle.find((b) => b.bereit === 0);
  if (laedt) return 'Die Vorschau-Datei lädt noch und liefert keine Kopfdaten. Meistens die Verbindung — sonst nimmt der Server keine Bereichs-Anfragen an.';
  const abgewiesen = mitQuelle.find((b) => b.pausiert && b.abgewiesen);
  if (abgewiesen) return 'Das Gerät hat das Abspielen verweigert. Auf dem iPhone heißt das: es kam nicht aus einer Berührung.';
  const ohneBild = mitQuelle.find((b) => b.breite === 0);
  if (ohneBild) return 'Die Datei ist da, aber es wurde noch kein einziges Bild daraus dekodiert.';
  // Erst wenn sonst nichts klemmt, ist das Ruckeln die Nachricht (7.57).
  const holprig = mitQuelle.find((b) => b.bilder > 60 && b.ausgelassen / b.bilder > 0.05);
  if (holprig) {
    return `Das Gerät kommt beim Dekodieren nicht nach: ${holprig.ausgelassen} von ${holprig.bilder} Bildern ausgelassen. Das ist Rechenlast, kein Fehler in der Wiedergabe.`;
  }
  const glatt = mitQuelle.find((b) => b.bilder > 60);
  if (glatt) {
    return `Das Bild läuft, und das Gerät kommt mit: ${glatt.ausgelassen} von ${glatt.bilder} Bildern ausgelassen. Ruckelt es trotzdem, liegt es nicht am Dekodieren.`;
  }
  return 'Das Bild sollte laufen. Steht es trotzdem still, sag mir bitte die Zahlen darunter.';
}
